import z from 'zod';
import { Big } from 'big.js';
import { coerceBoolean, zodNativeEnumOptional, zodTDateISODate, zodTTime24 } from '~/lib/zod';
import { InvitedUserSchema } from '~/lib/schema/user';
import { AppointmentCancel, AppointmentLock } from '~/lib/enum';

export const LatLngSchema = z.object({
  lat: z.coerce.number(),
  long: z.coerce.number(),
});

export type LatLng = z.infer<typeof LatLngSchema>;

const ADDRESS_ERROR = { required_error: 'Please lookup a valid address.' };

export const GeocodedAddressSchema = z.object(
  {
    line1: z.string(ADDRESS_ERROR),
    line2: z.coerce.string(ADDRESS_ERROR).optional().nullable(),
    city: z.string(ADDRESS_ERROR),
    // Only set for addresses geocoded since 9/19/26, and only where
    // Google knows one — never required, never shown empty.
    neighborhood: z.string().optional(),
    state: z.string(ADDRESS_ERROR),
    zip: z.coerce.string(ADDRESS_ERROR),
    ...LatLngSchema.shape,
  },
  ADDRESS_ERROR,
);

export const DistanceAddressSchema = GeocodedAddressSchema.extend({
  distance: z.coerce.number().nullable(),
  time: z.coerce.number().optional(),
});

export type GeocodedAddress = z.infer<typeof GeocodedAddressSchema>;
export type DistanceAddress = z.infer<typeof DistanceAddressSchema>;

export const GeocodedAddressExample: DistanceAddress = {
  line1: '2 Bank Street',
  line2: null,
  city: 'New London',
  state: 'CT',
  zip: '06320',
  long: -72.09381669999999,
  lat: 41.3539585,
  distance: null,
};

export type Address = Omit<GeocodedAddress, 'latitude' | 'longitude'>;

export type OrderLinePhase = 'submitted' | 'delivered' | 'completed';

export interface TenantOrderLine {
  id: string;
  amount: Big;
  compare?: boolean | string;
  description: string;
  discount?: boolean;
  phase?: OrderLinePhase;
  taxable?: boolean;
  estimate?: boolean;
  discountable?: boolean;
  job_id?: string;
  appointment_id?: string;
  provider_id?: string;
  fee?: 'holiday' | 'weekend' | 'travel' | 'rush' | 'tod' | 'delivery';
}

export type TenantJobLine = Omit<TenantOrderLine, 'job_id'>;

export function addressToStreet(address: Address): string {
  return `${address.line1 || ''}${address.line2 ? ` ${address.line2.replace(/^##/, '#')}` : ''}`;
}

export function addressToArea(address: Address): string {
  return `${address.city || ''}, ${address.state || ''} ${address.zip || ''}`;
}

export function addressToFull(address: Address): string {
  return `${addressToStreet(address)}, ${addressToArea(address)}`;
}

export function phoneFormat(phone: string): string {
  return phone.replace(/\+1(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
}

export type DoJson<T> = {
  [K in keyof T]: Required<T>[K] extends Date ? string : T[K] extends object ? DoJson<T[K]> : T[K];
};

export const ScheduleUpdateSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('create_placeholder'),
    event_id: z.string(),
    provider_id: z.string(),
    description: z.string(),
    start: z.coerce.date(),
    duration: z.coerce.number()
  }),
  z.object({
    type: z.literal('update_placeholder'),
    event_id: z.string(),
    provider_id: z.string(),
    description: z.string(),
    start: z.coerce.date(),
    duration: z.coerce.number(),
  }),
  z.object({
    type: z.literal('remove_placeholder'),
    event_id: z.string(),
  }),
  z.object({
    type: z.literal('unassociate_job'),
    order_id: z.string(),
    appointment_id: z.coerce.string(),
    job_id: z.coerce.string(),
  }),
  z.object({
    type: z.literal('delete_draft'),
    order_id: z.string(),
  }),
  z.object({
    type: z.literal('unschedule_appointment'),
    order_id: z.string(),
    appointment_id: z.coerce.string(),
  }),
  z.object({
    type: z.literal('cancel_appointment'),
    order_id: z.string(),
    appointment_id: z.coerce.string(),
    canceled: z.nativeEnum(AppointmentCancel),
    cancel_details: z.string().optional(),
  }),
  z.object({
    type: z.literal('associate_job'),
    order_id: z.string(),
    appointment_id: z.coerce.string(),
    job_id: z.coerce.string(),
    minutes: z.coerce.number(),
  }),
  z.object({
    type: z.literal('create_appointment'),
    name: z.coerce.string().optional(),
    description: z.coerce.string().optional(),
    start: z.coerce.date(),
    order_id: z.string(),
    appointment_id: z.coerce.string(),
    provider_id: z.string(),
    override: z.coerce.number().optional(),
    locked: zodNativeEnumOptional(AppointmentLock, true),
  }),
  z.object({
    type: z.literal('move_locked_appointment'),
    start: z.coerce.date(),
    order_id: z.string(),
    appointment_id: z.coerce.string(),
    provider_id: z.string(),
    override: z.coerce.number().optional(),
    locked: zodNativeEnumOptional(AppointmentLock, true),
  }),
  z.object({
    type: z.literal('move_unlocked_appointment'),
    start: z.coerce.date(),
    order_id: z.string(),
    appointment_id: z.coerce.string(),
    provider_id: z.string(),
    override: z.coerce.number().optional(),
    locked: zodNativeEnumOptional(AppointmentLock, true),
  }),
]);

export type ScheduleUpdate = z.infer<typeof ScheduleUpdateSchema>;

export const ScheduleUpdatesSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('update'),
    updates: ScheduleUpdateSchema.array(),
    notify_customer: coerceBoolean(),
    notify_provider: coerceBoolean(),
  }),
  z.object({
    type: z.literal('notify'),
  }),
]);

export type ScheduleUpdates = z.infer<typeof ScheduleUpdatesSchema>;

export class SessionState<D = Record<string, unknown>> {
  name!: string;
  data!: D;
}

export interface MapRoute {
  lat: number;
  lng: number;
  time: string;
}

export const UserTimeSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('day'),
    day: zodTDateISODate(),
  }),
  z.object({
    type: z.literal('custom'),
    day: zodTDateISODate(),
    notes: z.string(),
  }),
  z.object({
    type: z.literal('after'),
    day: zodTDateISODate(),
    time: zodTTime24('Please provide an after time.'),
  }),
  z.object({
    type: z.literal('before'),
    day: zodTDateISODate(),
    time: zodTTime24('Please provide a before time.'),
  }),
  z.object({
    type: z.literal('between'),
    day: zodTDateISODate(),
    start: zodTTime24('Please provide a start time.'),
    end: zodTTime24('Please provide an end time.'),
  }),
]);

export type UserTime = z.infer<typeof UserTimeSchema>;

export interface DisplayData {
  name: string;
  invoice?: boolean;
  provider?: boolean;
  /**
   * `false` keeps a provider row out of the job rows of the appointment's
   * calendar invite (display_invite_rows). That text is hashed, and a
   * changed hash re-sends the invite to the provider
   * (AppointmentEntity.sync_calendar_event), so a row that BECOMES
   * provider-visible on a job type with accepted appointments would re-send
   * every one of them. Such a row takes this.
   */
  invite?: boolean;
  customer?: boolean;
  schedule?: boolean;
  office?: boolean;
  email?: boolean;
  acknowledge?: boolean;
  highlight?: boolean;
  simple?: string;
  value: string | string[];
  deliverable_id?: string;
}

/**
 * The job rows a provider's calendar invite has always listed: the provider
 * rows, less any added since with `invite: false`.
 */
export function display_invite_rows(info: DisplayData[]): DisplayData[] {
  return info.filter((i) => i.provider && i.invite !== false);
}

// based on luxon
export enum BusinessHoursDay {
  Sunday = 7,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

export const BusinessHoursSchema = z.object({
  start: zodTTime24('Please provide a start time.'),
  end: zodTTime24('Please provide an end time.'),
});

export type BusinessHours = z.infer<typeof BusinessHoursSchema>;

export interface OrderInvoiceLine {
  id: string;
  amount: TMoney;
  description: string;
  original: TMoney | null;
  hidden: boolean;
}

export interface ProviderStaticConfig {
  first: string;
  last: string;
  color?: string;
  hidden?: boolean;
  headshot: string;
  location: string;
  editor?: boolean;
  email: TEmail;
  phone?: TPhone;
  schedule?: boolean;
  preference?: boolean;
  start_address: GeocodedAddress;
  end_address?: GeocodedAddress;
  travel_start?: TTime24;
  travel_start_ideal?: TTime24;
  travel_end?: TTime24;
  travel_end_ideal?: TTime24;
  schedule_start?: TTime24;
  schedule_end?: TTime24;
  /**
   * The services the office ticked on a DB-ONLY provider's profile (a provider
   * with no code record), expanded to every alias the tenant's performables
   * use (expandServiceIds). Set only by the dynamic overlay, and only for such
   * providers: code providers declare what they do in their own per-tenant
   * flags and never carry it.
   */
  profile_services?: string[];
}

/** Fields of a provider's code record that never ship to the browser: how to reach them, where they live, what they are paid. */
export type ProviderPrivateKey = 'email' | 'phone' | 'start_address' | 'end_address' | 'expense_appointment';

/** The server-only half of a code provider record (tenants/<t>/providers.server.ts). */
export interface ProviderPrivate {
  email: TEmail;
  phone?: TPhone;
  start_address: GeocodedAddress;
  end_address?: GeocodedAddress;
  /** MPI: what the provider is paid per appointment. */
  expense_appointment?: number;
}

/** The public half of a code provider record (tenants/<t>/data.ts): safe in the client bundle. */
export type ProviderPublic<T> = Omit<T, ProviderPrivateKey>;

export const ProviderDynamicUserSchema = InvitedUserSchema;

export type ProviderDynamicUser = z.output<typeof ProviderDynamicUserSchema>;

export const ProviderDynamicConfigSchema = z.object({
  nylas_calendar_id: z.string().optional(),
  nylas_grant_id: z.string().optional(),
  nylas_access_token: z.string().optional(),
  nylas_refresh_token: z.string().optional(),
  nylas_expires_in: z.number().optional(),
  users: z.record(z.string(), ProviderDynamicUserSchema).default({}),
});

export type ProviderDynamicConfig = z.infer<typeof ProviderDynamicConfigSchema>;

export const PROVIDER_DATA_DEFAULT = (): ProviderDynamicConfig => ({ users: {} });
